import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Link, useLocation} from "react-router-dom";
import {useFormik} from 'formik';
import {useHistory} from "react-router-dom";
import {useAlert} from 'react-alert'
import {AuthContext} from "../providers/AuthProvider";
import apiHelper from "../api/ApiHelper";
import {Redirect} from "react-router";
import {LoginSchema} from "../common/types/Schemas";
import {BYInput} from "../common/inputs/BYInput";
import Footer from "../common/views/Footer";
import AuthCode from "react-auth-code-input";
import { BYPasswordInput } from '../common/inputs/BYPasswordInput';

const Login = () => {
    const history = useHistory();
    const alert = useAlert();
    const {search} = useLocation();
    const {login, user} = useContext(AuthContext);
    const [rememberMe, setRememberMe] = useState(true);
    const [showTokenInput, setShowTokenInput] = useState(false);

    const queryParams = useMemo(() => new URLSearchParams(search), [search]);

    const formik = useFormik({
        initialValues: {
            username: localStorage.getItem('rm') || '',
            password: '',
            mfaToken: ''
        },
        validationSchema: LoginSchema,
        onSubmit: async (values) => {
            try {
                await login(values);

                if (rememberMe) {
                    localStorage.setItem('rm', values.username);
                } else {
                    localStorage.removeItem('rm');
                }

                const redirect = queryParams.get('r') ? decodeURIComponent(queryParams.get('r')) : undefined;

                if (redirect) {
                    return history.push(redirect);
                }

                history.push("/");
            } catch (error) {
                const statusCode = error.response && error.response.status;

                if (error.message === 'MFA_TOKEN_SENT') {
                    setShowTokenInput(true);
                } else if (error.message === 'MFA_TOKEN_INVALID') {
                    alert.error('Code d\'accès invalid');
                } else if (statusCode === 401) {
                    alert.error('Identifiant ou mot de passe invalide');
                } else if (statusCode === 403) {
                    alert.error('Utilisateur desáctivé');
                } else if (statusCode === 409) {
                    alert.error('Trop de tentatives de connexion échouées. Veuillez réessayer plus tard');
                } else if (statusCode === 429) {
                    alert.error('Trop de demandes, veuillez réessayer plus tard');
                } else {
                    alert.error(error.message);
                }
            }
        }
    });

    useEffect(() => {
        const token = queryParams.get('t');

        if (token) {
            apiHelper.activateForgotPasswordToken({token})
                .then(() => {
                    alert.success('Nouveau mot de passe activé');
                }).catch(() => {
                alert.error('Erreur lors de la génération du nouveau mot de passe');
            })
        }
    }, [alert, queryParams]);

    if (user) {
        return <Redirect to='/'/>
    }

    return (
        <section className="container-fluid">
            <div className="row">
                <div className="col-md-8 p-0 login-background d-none d-md-block"/>
                <div className="col-md-4 login-container">
                    <div className="p-md-5 p-4">
                        <div className="text-center mb-2">
                            <img className="logo-bi"
                                 src={process.env.PUBLIC_URL + '/img/logo.svg'} width={"200 px"}
                                 alt="Bouygues Immobilier logo"/>
                        </div>
                        <h3 className="text-center mb-4 animate__animated animate__bounce">Content de vous revoir
                            !</h3>
                        <form onSubmit={formik.handleSubmit}>
                            {!showTokenInput && <>
                                <div className="mb-3">
                                    <BYInput
                                        type="text"
                                        name="username"
                                        className="form-control"
                                        title="Identifiant"
                                        placeholder="Identifiant"
                                        value={formik.values.username}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        required
                                        autoFocus
                                        touched={formik.touched.username}
                                        error={formik.errors.username}
                                    />
                                </div>

                                <div className="mb-3">
                                    <BYPasswordInput
                                        name="password"
                                        value={formik.values.password}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        required
                                        touched={formik.touched.password}
                                        error={formik.errors.password}
                                    />
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="indicActif"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        id="flexCheckDefault"
                                    />
                                    <label className="form-check-label" htmlFor="flexCheckDefault">
                                        {' '}Se souvenir de moi
                                    </label>
                                </div>
                            </>}

                            {showTokenInput && <div className="mb-3">
                                <div className="alert alert-warning font-weight-bold mb-3">Un code d'accès a été envoyé
                                    à votre email. <br/>Saisissez-le pour vous connecter s'il vous plaît
                                </div>
                                <div className="text-center flex">
                                <label className="form-label">Code d'accès</label>
                                <AuthCode containerClassName="mfa-token-input" length={6} onChange={(mfaToken) => {
                                    formik.setValues({
                                        ...formik.values,
                                        mfaToken
                                    })
                                }}/>
                                </div>
                            </div>}

                            <div className="d-flex mt-5">
                                <button className="btn btn-secondary flex-grow-1" type="submit"
                                        disabled={formik.isSubmitting || (showTokenInput && formik.values.mfaToken.length !== 6)}>
                                    Connexion
                                </button>
                            </div>
                        </form>

                        <hr/>
                        <div>
                            <Link className="btn btn-link d-block" to='/recover_password'>
                                Vous avez oublié votre mot de passe ?
                            </Link>
                        </div>

                        <div className="link-brand justify-content-center mt-4">
                            <img className="navbar-brand" src={process.env.PUBLIC_URL + '/img/rezerve_logo.png'}
                                 alt="Bouygues Immobilier logo"/>
                            <h4 className="mb-0">eZerv</h4>
                        </div>
                    </div>

                    <Footer/>
                </div>
            </div>
        </section>
    )
};

export default Login;
